import React from 'react';
import { graphql } from 'gatsby';

import Layout from '../components/Layout';
import PostTitle from '../components/Title/PostTitle';
import SocialShare from '../components/SocialShare';

import { SectionLayout } from '../components/Layouts';
import * as T from '../components/Typography';
import { Row, Col } from '../components/Grid';
import Image from '../components/PreviewCompatibleImage';
import ActionCardsSection from '../sections/ActionCards';
import { ProgrammePostSection } from '../sections/CardsSections';
import { GeneralCardsSection } from '../sections/Cards';

import placeholderData from '../utils/placeholderData';

import * as S from './style';

export const ProgrammePostTemplate = ({
  category,
  title,
  content,
  programmeFocus,
  getInvolved,
  funder,
  funderLogo,
  imageInfo,
  id,
  ProgrammesPostsTitle,
  preview,
}) => {
  const renderSideDetails = (
    <>
      {programmeFocus && (
        <S.AsideInfoContainer mt={5} mtT={0} mtM={5}>
          <T.H6Caps mb={0.5} color="pink">
            Programme focus
          </T.H6Caps>
          <T.P16 color="gray3">{programmeFocus}</T.P16>
        </S.AsideInfoContainer>
      )}
      {getInvolved && (
        <S.AsideInfoContainer mt={4}>
          <T.H6Caps mb={0.5} color="pink">
            Who can get involved
          </T.H6Caps>
          <T.P16 color="gray3">{getInvolved}</T.P16>
        </S.AsideInfoContainer>
      )}
      {funder && (
        <S.AsideInfoContainer mt={4}>
          <T.H6Caps mb={0.5} color="pink">
            Funder
          </T.H6Caps>
          <T.P16 color="gray3">{funder}</T.P16>
          <S.LogoContainer mt={4}>
            <Image imageInfo={funderLogo} style={{ maxHeight: '177px' }} />
          </S.LogoContainer>
        </S.AsideInfoContainer>
      )}
    </>
  );

  return (
    <>
      <SectionLayout>
        <Row>
          <Col mb="1.5rem" w={[4, 6, 8]}>
            <PostTitle category={category}>{title}</PostTitle>
          </Col>
        </Row>
        <S.PostDesktopWrapper>
          <Row jc="space-between">
            <Col w={[4, 6, 7]}>
              <S.Content>
                {content && (
                  <S.PostsContentContainer
                    dangerouslySetInnerHTML={{
                      __html: content,
                    }}
                  />
                )}
                {/* social share */}
                <Col mt={5} ml="-0.7rem" w={[4, 2, 3]}>
                  <SocialShare />
                </Col>
              </S.Content>
            </Col>
            <Col w={[4, 6, 4]}>
              <S.ImgContainer>
                <Image imageInfo={imageInfo} style={{ maxHeight: '250px' }} />
              </S.ImgContainer>
              {renderSideDetails}
            </Col>
          </Row>
        </S.PostDesktopWrapper>

        <S.PostTabletWrapper>
          <Row>
            <Col w={[4, 3, 8]}>{renderSideDetails}</Col>
            <Col w={[4, 3, 8]}>
              <S.ImgContainer>
                <Image imageInfo={imageInfo} />
              </S.ImgContainer>
            </Col>

            <Col w={[4, 6, 8]}>
              {content && (
                <S.PostsContentContainer
                  mtT={5}
                  dangerouslySetInnerHTML={{
                    __html: content,
                  }}
                />
              )}
            </Col>
          </Row>
          {/* social share */}

          <Col mt={5} w={[4, 2, 3]}>
            <SocialShare />
          </Col>
        </S.PostTabletWrapper>

        <S.PostMobileWrapper>
          <Row>
            <Col w={[4, 3, 8]}>
              <S.ImgContainer>
                <Image imageInfo={imageInfo} />
              </S.ImgContainer>
              <Col w={[4, 3, 8]}>{renderSideDetails}</Col>
            </Col>

            <Col w={[4, 6, 8]}>
              {content && (
                <S.PostsContentContainer
                  mtM={6}
                  dangerouslySetInnerHTML={{
                    __html: content,
                  }}
                />
              )}
            </Col>
          </Row>
          {/* social share */}
          <Row>
            <Col mt={5} w={[4, 2, 3]}>
              <SocialShare jc="center" />
            </Col>
          </Row>
        </S.PostMobileWrapper>
      </SectionLayout>

      {/* impacts and updates  */}

      {preview ? (
        <GeneralCardsSection
          data={placeholderData}
          type="programmes"
          title={ProgrammesPostsTitle || 'Our programmes'}
        />
      ) : (
        <ProgrammePostSection
          currentId={id}
          title={ProgrammesPostsTitle || 'Our programmes'}
          type="programmes"
          bgcolor="gray2"
        />
      )}

      <ActionCardsSection />
    </>
  );
};

const ProgrammePostPage = ({ data }) => {
  const {
    markdownRemark: {
      html: content,
      id,
      frontmatter: {
        category,
        title,
        programmeFocus,
        getInvolved,
        funder,
        funderLogo,
        postImage,
        ProgrammesPostsTitle,
      },
    },
  } = data;

  return (
    <Layout isPost>
      <ProgrammePostTemplate
        content={content}
        title={title}
        category={category}
        programmeFocus={programmeFocus}
        getInvolved={getInvolved}
        funder={funder}
        imageInfo={postImage}
        funderLogo={funderLogo}
        id={id}
        ProgrammesPostsTitle={ProgrammesPostsTitle}
      />
    </Layout>
  );
};

export const programmePost = graphql`
  query programmePostQuery($id: String!) {
    markdownRemark(id: { eq: $id }) {
      id
      html
      frontmatter {
        category
        title
        programmeFocus
        getInvolved
        funder
        ProgrammesPostsTitle
        funderLogo {
          childImageSharp {
            fluid(quality: 100, maxWidth: 177) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        postImage {
          childImageSharp {
            fluid(quality: 100, maxWidth: 360) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    }
  }
`;

export default ProgrammePostPage;
